// Small trend drawn at its real pixel width, so the dot never stretches. An ink line, the last point marked.
// Scores are 0-100; the y-range hugs the data so small gains stay visible, or is `span` points tall so lines drawn side
// by side compare (a steeper line is a bigger gain). A skill's full chart is TrendChart.
import { useLayoutEffect, useRef, useState } from 'react';
import { cx } from '@/lib/cx';

type SparkOptions = {
  width: number;
  goal?: number | undefined;
  span?: number | undefined;
  compact?: boolean | undefined;
};

/** Pure geometry for the chart, so it can be tested without a browser. */
export function sparkGeometry(values: readonly number[], { width, goal, span, compact }: SparkOptions) {
  const w = Math.max(compact ? 48 : 120, Math.round(width));
  const h = compact ? 32 : 56;
  const pad = compact ? 4 : 6;
  const all = goal === undefined ? values : [...values, goal];
  let lo = Math.min(...all) - 4;
  let hi = Math.max(...all) + 4;
  const fixed = span !== undefined && Math.max(...values) - Math.min(...values) <= span;
  const height = fixed ? span : Math.max(16, hi - lo);
  const mid = fixed ? (Math.min(...values) + Math.max(...values)) / 2 : (hi + lo) / 2;
  lo = mid - height / 2;
  hi = mid + height / 2;
  const x = (i: number) => pad + (values.length === 1 ? (w - 2 * pad) / 2 : (i * (w - 2 * pad)) / (values.length - 1));
  const y = (v: number) => pad + (1 - (v - lo) / (hi - lo)) * (h - 2 * pad);
  const last = values.length - 1;
  return {
    w,
    h,
    pad,
    line: values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' '),
    dot: { x: x(last), y: y(values[last] ?? 0) },
    goalY: goal === undefined || fixed ? null : y(goal),
  };
}

type SparklineProps = {
  /** Oldest first. */
  values: readonly number[];
  /** Target, drawn as a dashed line (not with `span`). */
  goal?: number | undefined;
  /** Draw every line this many points tall, so lines side by side compare. */
  span?: number;
  /** 32px tall, for a row in a list. */
  compact?: boolean;
  /** What the line shows, for screen readers. */
  label?: string;
};

export function Sparkline({ values, goal, span, compact, label }: SparklineProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(compact ? 72 : 300);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (values.length === 0) return null;
  const g = sparkGeometry(values, { width, goal, span, compact });
  const description = label ?? `Trend: ${values.join(', ')}${goal === undefined ? '' : `; target ${goal}`}`;

  return (
    <div className="spark-wrap" ref={wrap}>
      <svg
        className={cx('spark', compact && 'compact')}
        width={g.w}
        height={g.h}
        viewBox={`0 0 ${g.w} ${g.h}`}
        role="img"
        aria-label={description}
      >
        {g.goalY !== null && <line className="goal" x1={g.pad} x2={g.w - g.pad} y1={g.goalY} y2={g.goalY} />}
        <polyline className="line" points={g.line} />
        <circle className="dot" cx={g.dot.x} cy={g.dot.y} r={compact ? 3 : 3.5} />
      </svg>
    </div>
  );
}
